/**
 * Display helpers shared by the inventory pages: exact rational quantities and
 * Persian labels for every enum the API returns. Dependency-free on purpose
 * (type-only imports) so the unit tests can import this file directly.
 *
 * Rule: a raw enum key (`in_stock`, `transfer_receive`, …) never reaches the
 * screen. An unknown key renders «نامشخص», not the key.
 */

export interface Quantity {
  readonly numerator: string;
  readonly denominator: string;
}

export type InvTone = 'neutral' | 'success' | 'warn' | 'danger' | 'info' | 'brand';

export interface LabelMeta {
  readonly label: string;
  readonly tone: InvTone;
}

export const UNKNOWN_LABEL = 'نامشخص';
const EMPTY = '—';

const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const toFa = (value: string): string => value.replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)] ?? d);

/**
 * Exact rational quantity → Persian text. BigInt only (AGENTS.md §8): a whole
 * number prints as itself, anything else as «صورت / مخرج» so no precision is
 * lost to a float. Unparseable input falls back to the raw digits, never NaN.
 */
export function formatQuantity(q: Quantity | null | undefined): string {
  if (!q) return EMPTY;
  try {
    const num = BigInt(q.numerator);
    const den = BigInt(q.denominator || '1');
    if (den === 0n) return EMPTY;
    if (den === 1n) return num.toLocaleString('fa-IR');
    return isolateLtr(`${num.toLocaleString('fa-IR')} / ${den.toLocaleString('fa-IR')}`);
  } catch {
    return isolateLtr(toFa(`${q.numerator} / ${q.denominator}`));
  }
}

/** A fraction must read «صورت / مخرج» left to right even inside RTL text, where it would flip. */
function isolateLtr(text: string): string {
  return `⁦${text}⁩`;
}

/** Persian (۰-۹) and Arabic-Indic (٠-٩) digits → ASCII, so typed quantities parse the same as pasted ones. */
export function normalizeDigits(value: string): string {
  return value
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}

/** A whole, non-negative quantity typed by the user (`""` and decimals are not quantities). */
export function parseWholeQuantity(text: string): Quantity | null {
  const digits = normalizeDigits(text).trim();
  return /^\d+$/.test(digits) ? { numerator: BigInt(digits).toString(), denominator: '1' } : null;
}

const absBig = (n: bigint): bigint => (n < 0n ? -n : n);

function gcd(a: bigint, b: bigint): bigint {
  let x = absBig(a);
  let y = absBig(b);
  while (y !== 0n) [x, y] = [y, x % y];
  return x;
}

/** `a − b` as an exact, reduced fraction; `null` when either side is not a valid quantity. */
export function subtractQuantity(a: Quantity, b: Quantity): Quantity | null {
  try {
    const ad = BigInt(a.denominator || '1');
    const bd = BigInt(b.denominator || '1');
    if (ad === 0n || bd === 0n) return null;
    const num = BigInt(a.numerator) * bd - BigInt(b.numerator) * ad;
    const den = ad * bd;
    const g = gcd(num, den) || 1n;
    return { numerator: (num / g).toString(), denominator: (den / g).toString() };
  } catch {
    return null;
  }
}

/** `a + b` as an exact, reduced fraction; `null` when either side is not a valid quantity. */
export function addQuantity(a: Quantity, b: Quantity): Quantity | null {
  return subtractQuantity(a, { numerator: negateText(b.numerator), denominator: b.denominator });
}

function negateText(numerator: string): string {
  try {
    return (-BigInt(numerator)).toString();
  } catch {
    return 'x';
  }
}

/** The whole-number part of a positive quantity as digits (`25/2` → `"12"`); `""` when it is not one. */
export function wholePartText(q: Quantity | null | undefined): string {
  if (!q) return '';
  try {
    const num = BigInt(q.numerator);
    const den = BigInt(q.denominator || '1');
    if (den <= 0n || num <= 0n) return '';
    return (num / den).toString();
  } catch {
    return '';
  }
}

/** -1, 0 or 1 by the exact sign of the quantity. */
export function quantitySign(q: Quantity): -1 | 0 | 1 {
  try {
    const n = BigInt(q.numerator);
    return n < 0n ? -1 : n > 0n ? 1 : 0;
  } catch {
    return 0;
  }
}

/** Same as `formatQuantity` for the flat `*_num` / `*_den` columns some list endpoints return. */
export function formatQuantityParts(numerator: string, denominator: string): string {
  return formatQuantity({ numerator, denominator });
}

/** True when the numerator is positive — exact comparison, no Number(). */
export function isPositiveQuantity(q: Quantity | null | undefined): boolean {
  if (!q) return false;
  try {
    return BigInt(q.numerator) > 0n;
  } catch {
    return false;
  }
}

export function metaOf(map: Readonly<Record<string, LabelMeta>>, key: string | null | undefined) {
  return (key ? map[key] : undefined) ?? { label: UNKNOWN_LABEL, tone: 'neutral' as InvTone };
}

export function labelOf(
  map: Readonly<Record<string, string>>,
  key: string | null | undefined,
): string {
  return (key ? map[key] : undefined) ?? UNKNOWN_LABEL;
}

export function optionsOf(
  map: Readonly<Record<string, LabelMeta | string>>,
  keys?: readonly string[],
): readonly { readonly value: string; readonly label: string }[] {
  return (keys ?? Object.keys(map)).map((value) => {
    const entry = map[value];
    return { value, label: typeof entry === 'string' ? entry : (entry?.label ?? UNKNOWN_LABEL) };
  });
}

// ---------------------------------------------------------------------------
// Lots and serials
// ---------------------------------------------------------------------------

export const LOT_STATUS_META: Readonly<Record<string, LabelMeta>> = {
  active: { label: 'فعال', tone: 'success' },
  quarantined: { label: 'قرنطینه', tone: 'warn' },
  released: { label: 'ترخیص‌شده', tone: 'info' },
  expired: { label: 'منقضی‌شده', tone: 'danger' },
  blocked: { label: 'مسدود', tone: 'danger' },
  depleted: { label: 'پایان‌یافته', tone: 'neutral' },
};

export const SERIAL_STATUS_META: Readonly<Record<string, LabelMeta>> = {
  in_stock: { label: 'موجود در انبار', tone: 'success' },
  available: { label: 'موجود در انبار', tone: 'success' },
  reserved: { label: 'رزرو شده', tone: 'warn' },
  delivered: { label: 'تحویل مشتری', tone: 'info' },
  issued: { label: 'خارج‌شده', tone: 'info' },
  returned: { label: 'برگشتی', tone: 'warn' },
  scrapped: { label: 'اسقاط', tone: 'danger' },
};

/** Statuses the serial filter offers (the two stock aliases collapse into one entry). */
export const SERIAL_FILTER_KEYS = [
  'in_stock',
  'reserved',
  'issued',
  'delivered',
  'returned',
  'scrapped',
] as const;

// ---------------------------------------------------------------------------
// Tracking contract
// ---------------------------------------------------------------------------

export const TRACKING_MODE_META: Readonly<Record<string, LabelMeta>> = {
  none: { label: 'بدون ردیابی', tone: 'neutral' },
  normal: { label: 'بدون ردیابی', tone: 'neutral' },
  batch: { label: 'ردیابی بهر', tone: 'info' },
  lot: { label: 'ردیابی بهر', tone: 'info' },
  serial: { label: 'سریال یکتا', tone: 'info' },
  batch_and_serial: { label: 'بهر و سریال', tone: 'warn' },
  expiry_required: { label: 'انقضا الزامی', tone: 'success' },
  expiry_optional: { label: 'انقضا اختیاری', tone: 'info' },
};

/** The six modes a contract can be saved with (`normal` / `lot` are legacy aliases). */
export const TRACKING_MODE_KEYS = [
  'none',
  'batch',
  'serial',
  'batch_and_serial',
  'expiry_required',
  'expiry_optional',
] as const;

/** `normal` / `lot` are legacy aliases the edit form must not offer; map them to the mode they mean. */
export function canonicalTrackingMode(key: string): string {
  if (key === 'normal') return 'none';
  if (key === 'lot') return 'batch';
  return key;
}

export const FEFO_POLICY_LABEL: Readonly<Record<string, string>> = {
  off: 'غیرفعال',
  relaxed: 'پیشنهادی',
  strict: 'اجباری',
};

export const FEFO_POLICY_KEYS = ['off', 'relaxed', 'strict'] as const;

// ---------------------------------------------------------------------------
// Stock status and reorder alerts
// ---------------------------------------------------------------------------

export type StockStatus = 'کافی' | 'کم‌موجود' | 'ناموجود';

/**
 * Real 3-state status from onHand vs. available, compared as exact fractions. No reorder-point
 * field is involved: it only tells zero / partly reserved-or-blocked / fully available apart.
 */
export function stockStatusOf(row: {
  readonly available: Quantity;
  readonly onHand: Quantity;
}): StockStatus {
  if (quantitySign(row.available) <= 0) return 'ناموجود';
  const gap = subtractQuantity(row.available, row.onHand);
  if (gap && quantitySign(gap) < 0) return 'کم‌موجود';
  return 'کافی';
}

export const REORDER_STATUS_META: Readonly<Record<string, LabelMeta>> = {
  critical: { label: 'کسری بحرانی', tone: 'danger' },
  reorder: { label: 'نیاز به سفارش', tone: 'warn' },
  surplus: { label: 'مازاد انبار', tone: 'info' },
  normal: { label: 'نرمال', tone: 'success' },
};

// ---------------------------------------------------------------------------
// Count sessions
// ---------------------------------------------------------------------------

export const COUNT_SESSION_STATUS_META: Readonly<Record<string, LabelMeta>> = {
  draft: { label: 'پیش‌نویس', tone: 'neutral' },
  in_progress: { label: 'در حال شمارش', tone: 'info' },
  reconciled: { label: 'مغایرت‌گیری‌شده', tone: 'warn' },
  posted: { label: 'ثبت نهایی', tone: 'success' },
  cancelled: { label: 'باطل‌شده', tone: 'neutral' },
};

/** Which `StatePanel` variant an API failure maps to. */
export function stateVariantFor(
  status: number,
): 'permission_denied' | 'not_found' | 'network_error' {
  if (status === 403) return 'permission_denied';
  if (status === 404) return 'not_found';
  return 'network_error';
}

// ---------------------------------------------------------------------------
// Reservations
// ---------------------------------------------------------------------------

export const RESERVATION_STATUS_META: Readonly<Record<string, LabelMeta>> = {
  active: { label: 'فعال', tone: 'info' },
  released: { label: 'آزادشده', tone: 'neutral' },
  consumed: { label: 'مصرف‌شده', tone: 'success' },
};

// ---------------------------------------------------------------------------
// Movements
// ---------------------------------------------------------------------------

export const MOVEMENT_STATUS_META: Readonly<Record<string, LabelMeta>> = {
  posted: { label: 'ثبت‌شده', tone: 'success' },
  in_transit: { label: 'کالا در راه', tone: 'warn' },
  received: { label: 'وصول‌شده در مقصد', tone: 'success' },
  cancelled: { label: 'لغوشده', tone: 'danger' },
};

export const MOVEMENT_TYPE_LABEL: Readonly<Record<string, string>> = {
  receipt: 'رسید انبار',
  issue: 'حواله خروج',
  transfer: 'انتقال بین‌انبار',
  count_adjustment: 'تعدیل انبارگردانی',
};

export const MOVEMENT_TYPE_META: Readonly<Record<string, LabelMeta>> = {
  receipt: { label: 'رسید انبار', tone: 'success' },
  issue: { label: 'حواله خروج', tone: 'info' },
  transfer: { label: 'انتقال بین‌انبار', tone: 'warn' },
  count_adjustment: { label: 'تعدیل انبارگردانی', tone: 'neutral' },
};

/** What a serial's lineage row did (`ledger/movement.ts` writes exactly these). */
export const LINEAGE_ACTION_LABEL: Readonly<Record<string, string>> = {
  receipt: 'ورود به انبار',
  issue: 'خروج از انبار',
  transfer: 'انتقال بین‌انبار',
  transfer_dispatch: 'ارسال انتقال',
  transfer_receive: 'وصول انتقال',
  count_adjustment: 'تعدیل انبارگردانی',
  delivery: 'تحویل به مشتری',
};

/** Where a movement came from. `sourceType` is free text server-side, so unknown → «سایر». */
export const SOURCE_TYPE_LABEL: Readonly<Record<string, string>> = {
  manual: 'دستی',
  'manual-entry': 'دستی',
  'manual-reservation': 'رزرو دستی',
  receipt: 'رسید',
  issue: 'خروج',
  transfer: 'انتقال',
  warehouse_transfer: 'انتقال بین‌انبار',
  delivery: 'تحویل',
  sales_issue: 'خروج فروش',
  sales_order: 'سفارش فروش',
  sales_return: 'مرجوعی فروش',
  sales_handoff: 'تحویل فروش',
  order: 'سفارش',
  invoice: 'فاکتور',
  adjustment: 'تعدیل',
  count_adjustment: 'تعدیل انبارگردانی',
};

export function sourceTypeLabel(key: string | null | undefined): string {
  return (key ? SOURCE_TYPE_LABEL[key] : undefined) ?? 'سایر';
}

// ---------------------------------------------------------------------------
// Master-data pickers
// ---------------------------------------------------------------------------

export interface PickerOption {
  readonly value: string;
  readonly label: string;
}

/**
 * Master-data rows are `Record<string, unknown>`; this reads one into a picker
 * option without trusting the shape. A row with no id is dropped.
 */
export function pickerOptions(
  items: readonly Record<string, unknown>[],
  idKey: string,
  withCode = true,
): readonly PickerOption[] {
  const out: PickerOption[] = [];
  for (const row of items) {
    const value = String(row[idKey] ?? '');
    if (!value) continue;
    const code = String(row['code'] ?? '');
    const name = String(row['name'] ?? code);
    out.push({ value, label: withCode && code && code !== name ? `${name} (${code})` : name });
  }
  return out;
}

/**
 * `GET inventory/stock-balances` returns the most recently updated rows, at most this many (the
 * default is 50, the ceiling 100) and has no paging or total. A response this long may be cut off.
 */
export const STOCK_BALANCES_LIMIT = 100;

export function isStockBalancesCapped(count: number): boolean {
  return count >= STOCK_BALANCES_LIMIT;
}

/** The pickers load `limit` rows; when the server has more, say so rather than hide them silently. */
export function isTruncated(shown: number, total: number | undefined): boolean {
  return typeof total === 'number' && total > shown;
}
